import { useState } from "react";
import { toast } from "sonner";
import { Check, EyeOff, Pencil, Plus, Trash2, ExternalLink, MessageCircle } from "lucide-react";
import { api, img, useApi, slugify, waLink, formatApiError } from "@/lib/api";
import { formatRange } from "@/lib/image";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SingleImage } from "@/pages/admin/ImageManager";
import { AdminHeader } from "@/pages/admin/AdminLayout";
import { SpanishFields, cleanEs } from "@/pages/admin/SpanishFields";

const ES_FIELDS = [{ key: "title", label: "Nombre del retiro", type: "input" }, { key: "venue", label: "Lugar", type: "input" }, { key: "description", label: "Descripción", rows: 4 }];
const today = () => new Date().toISOString().slice(0, 10);
const BLANK = { slug: "", title: "", image: "", start_date: today(), end_date: today(), venue: "", description: "", whatsapp: "", info_url: "", status: "published", source: "owner", es: {} };
const TABS = [["pending", "Pending approval"], ["published", "Published"], ["past", "Past"], ["rejected", "Declined"]];
const tabOf = (r) => (r.status === "published" && r.end_date < today() ? "past" : r.status);
const STATUS = { pending: "bg-amber-100 text-amber-900", published: "bg-forest text-white", rejected: "bg-clay text-ink/70", past: "bg-linen text-moss" };

const F = ({ label, children }) => <div className="space-y-1.5"><Label className="font-bold">{label}</Label>{children}</div>;

const RetreatForm = ({ item, onSaved }) => {
  const [f, setF] = useState(item);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    if (!f.image) return toast.error("Please upload a photo");
    if (f.end_date < f.start_date) return toast.error("End date must be on or after the start date");
    const body = { ...f, slug: f.slug || slugify(f.title), es: cleanEs(f.es) };
    try {
      if (f.id) await api.put(`/admin/retreats/${f.id}`, body);
      else await api.post("/admin/retreats", body);
      toast.success("Retreat saved");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <form onSubmit={save} className="space-y-4" data-testid="retreat-form">
      <F label="Retreat name"><Input required value={f.title} onChange={set("title")} data-testid="retreat-field-title" /></F>
      <SingleImage value={f.image} onChange={(v) => setF({ ...f, image: v })} testId="retreat-image-upload" />
      <div className="grid gap-4 sm:grid-cols-2">
        <F label="Start date"><Input required type="date" value={f.start_date} onChange={set("start_date")} data-testid="retreat-field-start" /></F>
        <F label="End date"><Input required type="date" min={f.start_date} value={f.end_date} onChange={set("end_date")} data-testid="retreat-field-end" /></F>
        <F label="Location / venue"><Input value={f.venue} onChange={set("venue")} data-testid="retreat-field-venue" /></F>
        <F label="WhatsApp number"><Input value={f.whatsapp} onChange={set("whatsapp")} data-testid="retreat-field-whatsapp" /></F>
        <F label="Tickets / info link"><Input value={f.info_url} onChange={set("info_url")} placeholder="https://" data-testid="retreat-field-url" /></F>
        <F label="URL slug"><Input value={f.slug} placeholder={slugify(f.title)} onChange={(e) => setF({ ...f, slug: slugify(e.target.value) })} data-testid="retreat-field-slug" /></F>
      </div>
      <F label="Short description"><Textarea rows={4} value={f.description} onChange={set("description")} data-testid="retreat-field-description" /></F>
      <F label="Status">
        <select value={f.status} onChange={set("status")} className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm" data-testid="retreat-field-status">
          <option value="published">Published (live on website)</option><option value="pending">Pending (not live)</option><option value="rejected">Declined (not live)</option>
        </select>
      </F>
      <SpanishFields fields={ES_FIELDS} source={f} value={f.es || {}} onChange={(es) => setF({ ...f, es })} testId="retreat-es" />
      <button type="submit" className="btn-primary w-full" data-testid="retreat-save">Save retreat</button>
    </form>
  );
};

const Row = ({ r, onStatus, onEdit, onRemove }) => {
  const tab = tabOf(r);
  const digits = (r.whatsapp || "").replace(/\D/g, "");
  return (
    <div className="flex flex-col gap-4 border-b border-clay p-4 last:border-b-0 sm:flex-row sm:items-center" data-testid={`admin-retreat-row-${r.id}`}>
      <img src={img(r.image)} alt="" className="h-20 w-28 shrink-0 rounded-lg object-cover" />
      <div className="min-w-0 flex-1">
        <p className="font-bold text-ink">{r.title} <span className={`ml-2 rounded-full px-2 py-0.5 text-[11px] font-bold ${STATUS[tab]}`}>{tab}</span>{r.source === "host" && <span className="ml-1 text-xs text-moss">· submitted by host</span>}</p>
        <p className="text-sm font-semibold text-sage">{formatRange(r.start_date, r.end_date, "en")} · {r.venue}</p>
        <p className="mt-1 line-clamp-2 text-sm text-moss">{r.description}</p>
        <div className="mt-1 flex flex-wrap gap-4 text-sm">
          {digits && <a href={waLink(digits)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-semibold text-forest hover:underline" data-testid={`retreat-wa-${r.id}`}><MessageCircle className="h-3.5 w-3.5" /> {r.whatsapp}</a>}
          {r.info_url && <a href={r.info_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-semibold text-forest hover:underline"><ExternalLink className="h-3.5 w-3.5" /> Info link</a>}
          {tab === "published" && <a href={`/retreats/${r.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-semibold text-forest hover:underline" data-testid={`retreat-view-${r.id}`}><ExternalLink className="h-3.5 w-3.5" /> View page</a>}
        </div>
      </div>
      <div className="flex shrink-0 gap-2">
        {r.status !== "published" && <button onClick={() => onStatus(r, "published")} className="btn-primary !px-4 !py-2" data-testid={`retreat-approve-${r.id}`}><Check className="h-4 w-4" /> Approve</button>}
        {r.status === "published" && <button onClick={() => onStatus(r, "pending")} className="btn-outline !px-4 !py-2" data-testid={`retreat-unpublish-${r.id}`}><EyeOff className="h-4 w-4" /> Unpublish</button>}
        {r.status === "pending" && <button onClick={() => onStatus(r, "rejected")} className="btn-outline !px-4 !py-2" data-testid={`retreat-decline-${r.id}`}>Decline</button>}
        <button onClick={() => onEdit(r)} className="rounded-lg border border-clay p-2.5 text-moss hover:text-forest" aria-label="Edit" data-testid={`retreat-edit-${r.id}`}><Pencil className="h-4 w-4" /></button>
        <button onClick={() => onRemove(r)} className="rounded-lg border border-clay p-2.5 text-moss hover:text-destructive" aria-label="Delete" data-testid={`retreat-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
  );
};

export default function AdminRetreats() {
  const { data, reload } = useApi("/admin/retreats", []);
  const [tab, setTab] = useState("pending");
  const [editing, setEditing] = useState(null);
  const run = async (fn, msg) => {
    try { await fn(); toast.success(msg); } catch (e) { toast.error(formatApiError(e)); }
    reload();
  };
  const onStatus = (r, status) => run(() => api.patch(`/admin/retreats/${r.id}`, { status }), status === "published" ? "Approved - now live on the website" : "Updated");
  const onRemove = (r) => window.confirm(`Delete "${r.title}"?`) && run(() => api.delete(`/admin/retreats/${r.id}`), "Deleted");
  const items = data.filter((r) => tabOf(r) === tab);
  return (
    <div data-testid="admin-retreats">
      <AdminHeader title="Retreats">
        <button onClick={() => setEditing({ ...BLANK })} className="btn-primary" data-testid="retreat-new"><Plus className="h-4 w-4" /> New retreat</button>
      </AdminHeader>
      <div className="mb-6 flex flex-wrap gap-2">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`retreat-tab-${k}`} className={`rounded-full px-4 py-2 text-sm font-bold ${tab === k ? "bg-forest text-white" : "border border-clay bg-white text-ink/75 hover:bg-linen"}`}>
            {l} <span className="opacity-70">({data.filter((r) => tabOf(r) === k).length})</span>
          </button>
        ))}
      </div>
      <div className="overflow-hidden rounded-2xl border border-clay bg-white" data-testid="retreats-admin-list">
        {items.map((r) => <Row key={r.id} r={r} onStatus={onStatus} onEdit={setEditing} onRemove={onRemove} />)}
        {!items.length && <p className="p-6 text-sm text-moss" data-testid="retreats-admin-empty">Nothing here.</p>}
      </div>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl font-black text-forest">{editing?.id ? "Edit retreat" : "New retreat"}</DialogTitle></DialogHeader>
          {editing && <RetreatForm item={editing} onSaved={() => { setEditing(null); reload(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
